import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Spinner, WAIcon, ADMIN_WA } from './academy/academyUtils';
import ChangePasswordCard from './academy/ChangePasswordCard';

const SKILLS = [
  {
    id: 'listening',
    icon: '🎧',
    label: 'Listening',
    color: '#1a5fa8',
    bg: '#e8f0fb',
    border: '#c0d8f8',
    desc: 'Full listening practice sections, audio materials, and recorded sessions with examiner guidance.',
    tabs: ['sessions', 'materials'],
  },
  {
    id: 'reading',
    icon: '📖',
    label: 'Reading',
    color: '#1a7a4a',
    bg: '#e8f5ee',
    border: '#c8ead8',
    desc: 'Academic and general reading passages, skimming and scanning techniques, timed practice.',
    tabs: ['sessions', 'materials'],
  },
  {
    id: 'speaking',
    icon: '🗣️',
    label: 'Speaking',
    color: '#b8620a',
    bg: '#fff8f0',
    border: '#f0d890',
    desc: 'Speaking mock exams with a certified IELTS examiner and full band-score feedback.',
    tabs: ['sessions', 'materials', 'mock', 'progress'],
    hasMockExam: true,
  },
  {
    id: 'writing1',
    icon: '📊',
    label: 'Writing Task 1',
    color: '#5a1a8a',
    bg: '#f5ebff',
    border: '#e0c8f8',
    desc: 'Charts, graphs, process diagrams, maps, and letter-writing techniques with feedback.',
    tabs: ['sessions', 'materials'],
  },
  {
    id: 'writing2',
    icon: '✍️',
    label: 'Writing Task 2',
    color: '#1a2a4a',
    bg: '#eef2f9',
    border: '#c0ccd8',
    desc: 'AI-powered essay feedback, sentence-by-sentence coaching, idea banks, and timed exam simulation.',
    tabs: ['sessions', 'materials', 'practice'],
    hasWritingTool: true,
  },
];

const MATERIAL_ICON = { recording:'🎥', book:'📖', file:'📄', practice:'✏️', link:'🔗' };

const SPEAKING_CRITERIA = [
  { key:'fluency_score',      label:'Fluency & Coherence',     color:'#1a5fa8', bg:'#e8f0fb' },
  { key:'lexical_score',      label:'Lexical Resource',        color:'#1a7a4a', bg:'#e8f5ee' },
  { key:'grammar_score',      label:'Grammatical Range',       color:'#5a1a8a', bg:'#f5ebff' },
  { key:'pronunciation_score',label:'Pronunciation',           color:'#b8620a', bg:'#fff8f0' },
];

// ── Material card ─────────────────────────────────────────────────────────────
function MaterialCard({ material, color }) {
  return (
    <a href={material.url} target="_blank" rel="noreferrer"
      style={{ background:'#fff', border:`1.5px solid ${color}22`, borderRadius:10, padding:'14px 18px', display:'flex', gap:14, alignItems:'center', textDecoration:'none', transition:'all .15s', cursor:'pointer' }}
      onMouseEnter={e=>{ e.currentTarget.style.borderColor=color+'66'; e.currentTarget.style.boxShadow=`0 4px 14px ${color}18`; }}
      onMouseLeave={e=>{ e.currentTarget.style.borderColor=color+'22'; e.currentTarget.style.boxShadow='none'; }}>
      <div style={{ fontSize:28, flexShrink:0 }}>{MATERIAL_ICON[material.type] || '📄'}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:600, fontSize:14, color:'#1a2233', marginBottom:2 }}>{material.title}</div>
        {material.description && <div style={{ fontSize:12.5, color:'#5a6a88', lineHeight:1.5 }}>{material.description}</div>}
      </div>
      <span style={{ fontSize:12, color:color, background:`${color}18`, border:`1px solid ${color}44`, borderRadius:100, padding:'3px 12px', fontWeight:600, flexShrink:0, whiteSpace:'nowrap' }}>Open →</span>
    </a>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ icon, title, desc, waHref }) {
  return (
    <div style={{ background:'#f8f9fb', border:'1.5px dashed #dde3ed', borderRadius:14, padding:'40px 24px', textAlign:'center' }}>
      <div style={{ fontSize:44, marginBottom:12 }}>{icon}</div>
      <div style={{ fontWeight:600, fontSize:15, color:'#1a2233', marginBottom:6 }}>{title}</div>
      <div style={{ fontSize:13, color:'#9aa3b8', lineHeight:1.7, marginBottom:20, maxWidth:380, margin:'0 auto 20px' }}>{desc}</div>
      {waHref && (
        <a href={waHref} target="_blank" rel="noreferrer"
          style={{ display:'inline-flex', alignItems:'center', gap:8, background:'#25d366', color:'#fff', padding:'10px 22px', borderRadius:9, fontSize:13.5, fontWeight:700, textDecoration:'none', boxShadow:'0 3px 10px rgba(37,211,102,0.3)' }}>
          <WAIcon size={14}/> Contact Mr. Ahmed
        </a>
      )}
    </div>
  );
}

// ── Band bar ──────────────────────────────────────────────────────────────────
function BandBar({ score, color, bg, label }) {
  const pct = Math.min(100, ((score || 0) / 9) * 100);
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
        <span style={{ fontSize:13, color:'#2a3a5a', fontWeight:500 }}>{label}</span>
        <span style={{ fontSize:14, fontWeight:700, color: score ? color : '#9aa3b8', fontFamily:"'JetBrains Mono',monospace" }}>
          {score ? score.toFixed(1) : '—'}
        </span>
      </div>
      <div style={{ background:'#eef2f9', borderRadius:100, height:8, overflow:'hidden' }}>
        <div style={{ width:`${pct}%`, background: score ? color : '#dde3ed', height:'100%', borderRadius:100, transition:'width .5s ease' }}/>
      </div>
    </div>
  );
}

// ── Speaking progress tracker ─────────────────────────────────────────────────
function SpeakingProgressView({ exams }) {
  if (!exams || exams.length === 0) {
    return (
      <div style={{ background:'#f8f9fb', border:'1.5px dashed #dde3ed', borderRadius:14, padding:'32px 24px', textAlign:'center' }}>
        <div style={{ fontSize:40, marginBottom:10 }}>📊</div>
        <div style={{ fontWeight:600, fontSize:14, color:'#1a2233', marginBottom:6 }}>No completed exams yet</div>
        <div style={{ fontSize:13, color:'#9aa3b8', lineHeight:1.65 }}>
          Your scores will appear here after your first Speaking Mock Exam is completed and reviewed by the examiner.
        </div>
      </div>
    );
  }

  const sorted = [...exams].sort((a,b)=>new Date(a.scheduled_at)-new Date(b.scheduled_at));
  const latest = sorted[sorted.length - 1];

  function avg(key) {
    const vals = exams.filter(e=>e[key]!=null).map(e=>e[key]);
    return vals.length ? vals.reduce((s,v)=>s+v,0)/vals.length : null;
  }

  function trend(key) {
    if (sorted.length < 2) return null;
    const prev = sorted[sorted.length - 2][key];
    const curr = sorted[sorted.length - 1][key];
    if (prev==null||curr==null) return null;
    if (curr > prev) return '↑';
    if (curr < prev) return '↓';
    return '→';
  }

  const overall = latest.band_score ||
    (SPEAKING_CRITERIA.map(c=>latest[c.key]).filter(v=>v!=null).reduce((s,v,_,a)=>s+v/a.length,0) || null);

  return (
    <div>
      {/* Latest scores */}
      <div style={{ background:'#fff', border:'1.5px solid #e8edf5', borderRadius:14, padding:'22px', marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:18, flexWrap:'wrap', gap:10 }}>
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:2 }}>Latest Exam Results</div>
            {latest.scheduled_at && (
              <div style={{ fontSize:12, color:'#9aa3b8' }}>
                {new Date(latest.scheduled_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}
              </div>
            )}
          </div>
          {overall && (
            <div style={{ background:'linear-gradient(135deg,#b8860b,#d4a017)', borderRadius:12, padding:'10px 20px', textAlign:'center' }}>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.75)', fontWeight:600, marginBottom:2, letterSpacing:'.06em' }}>OVERALL BAND</div>
              <div style={{ fontSize:26, fontWeight:700, color:'#fff', fontFamily:"'JetBrains Mono',monospace", lineHeight:1 }}>{overall.toFixed(1)}</div>
            </div>
          )}
        </div>
        {SPEAKING_CRITERIA.map(c => (
          <div key={c.key}>
            <BandBar score={latest[c.key]} color={c.color} bg={c.bg} label={
              <span>{c.label} {trend(c.key) && <span style={{ fontSize:12, marginLeft:4, color: trend(c.key)==='↑'?'#1a7a4a': trend(c.key)==='↓'?'#c0392b':'#9aa3b8' }}>{trend(c.key)}</span>}</span>
            }/>
          </div>
        ))}
        {latest.feedback && (
          <div style={{ background:'#f7f9fc', border:'1px solid #dde3ed', borderRadius:9, padding:'12px 16px', marginTop:6 }}>
            <div style={{ fontSize:11.5, fontWeight:600, color:'#5a6a88', marginBottom:4 }}>Examiner Feedback</div>
            <div style={{ fontSize:13, color:'#2a3a5a', lineHeight:1.65 }}>{latest.feedback}</div>
          </div>
        )}
      </div>

      {/* Averages across all exams */}
      {exams.length > 1 && (
        <div style={{ background:'#fff', border:'1.5px solid #e8edf5', borderRadius:14, padding:'22px', marginBottom:16 }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, fontWeight:700, color:'#1a2a4a', marginBottom:14 }}>Overall Averages ({exams.length} exams)</div>
          {SPEAKING_CRITERIA.map(c => <BandBar key={c.key} score={avg(c.key)} color={c.color} bg={c.bg} label={c.label}/>)}
        </div>
      )}

      {/* Exam history */}
      <div style={{ background:'#fff', border:'1.5px solid #e8edf5', borderRadius:14, padding:'22px' }}>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, fontWeight:700, color:'#1a2a4a', marginBottom:14 }}>Exam History</div>
        {sorted.map((e, i) => {
          const band = e.band_score || (SPEAKING_CRITERIA.map(c=>e[c.key]).filter(v=>v!=null).reduce((s,v,_,a)=>s+v/a.length,0) || null);
          return (
            <div key={e.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom: i < sorted.length-1 ? '1px solid #f0f4f8' : 'none', flexWrap:'wrap', gap:8 }}>
              <div>
                <div style={{ fontSize:13.5, fontWeight:600, color:'#1a2233' }}>
                  Exam {i+1} {i === sorted.length-1 ? <span style={{ fontSize:10, background:'#e8f0fb', color:'#1a5fa8', borderRadius:100, padding:'2px 8px', marginLeft:6, fontWeight:700 }}>Latest</span> : ''}
                </div>
                {e.scheduled_at && <div style={{ fontSize:12, color:'#5a6a88' }}>{new Date(e.scheduled_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}</div>}
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {SPEAKING_CRITERIA.map(c => e[c.key]!=null && (
                  <div key={c.key} style={{ background:c.bg, borderRadius:8, padding:'4px 10px', textAlign:'center' }}>
                    <div style={{ fontSize:9, color:c.color, fontWeight:600 }}>{c.label.split(' ')[0]}</div>
                    <div style={{ fontSize:13, fontWeight:700, color:c.color, fontFamily:"'JetBrains Mono',monospace" }}>{e[c.key].toFixed(1)}</div>
                  </div>
                ))}
                {band && (
                  <div style={{ background:'#fff8e8', borderRadius:8, padding:'4px 10px', textAlign:'center', border:'1px solid #f0d890' }}>
                    <div style={{ fontSize:9, color:'#b8860b', fontWeight:600 }}>Overall</div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#b8860b', fontFamily:"'JetBrains Mono',monospace" }}>{band.toFixed(1)}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Skill detail view ─────────────────────────────────────────────────────────
function SkillView({ skill, student, tier, speakingExams, onBack, onWritingTool, onProgressTracker }) {
  const [activeTab, setActiveTab]   = useState(skill.tabs[0]);
  const [materials, setMaterials]   = useState([]);
  const [loadingM,  setLoadingM]    = useState(false);

  useEffect(() => {
    setLoadingM(true);
    async function load() {
      try {
        const { data } = await supabase
          .from('course_materials')
          .select('*')
          .eq('skill', skill.id)
          .eq('is_published', true)
          .order('sort_order');
        setMaterials(data || []);
      } catch {}
      setLoadingM(false);
    }
    load();
  }, [skill.id]);

  const waMsg = (msg) =>
    `https://wa.me/${ADMIN_WA}?text=${encodeURIComponent(`Hello Mr. Ahmed! My name is ${student?.name || 'a student'}. ${msg}`)}`;

  const recordings = materials.filter(m => m.type === 'recording');
  const docs        = materials.filter(m => ['book','file','link','practice'].includes(m.type));

  const TAB_LABELS = {
    sessions:  '📅 Sessions & Recordings',
    materials: '📚 Books & Materials',
    practice:  '✍️ AI Practice Tool',
    mock:      '🎯 Book Mock Exam',
    progress:  '📈 My Progress',
  };

  const completedExams = (speakingExams || []).filter(e => e.status === 'completed');
  const speakingIncluded = tier?.speaking_exams_included;
  const speakingUsed = completedExams.length;

  return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>

      {/* Skill header */}
      <div style={{ background:'linear-gradient(135deg,#1a2a4a,#2a3a6a)', padding:'14px 20px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
          <div style={{ background:'rgba(255,255,255,0.12)', borderRadius:7, padding:'3px 7px', display:'flex', alignItems:'center' }}>
            <img src="/logo.png" alt="IELTS Compass" style={{ height:26, objectFit:'contain' }}/>
          </div>
          <button onClick={onBack}
            style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', color:'rgba(255,255,255,0.8)', padding:'6px 14px', borderRadius:7, cursor:'pointer', fontSize:12, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            ← My Dashboard
          </button>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <div style={{ fontSize:40 }}>{skill.icon}</div>
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, color:'#fff' }}>{skill.label}</div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.6)', maxWidth:520, lineHeight:1.5 }}>{skill.desc}</div>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ background:'#fff', borderBottom:'1px solid #dde3ed', display:'flex', padding:'0 20px', overflowX:'auto' }}>
        {skill.tabs.map(k => (
          <button key={k} onClick={() => setActiveTab(k)}
            style={{ border:'none', borderBottom:`2.5px solid ${activeTab===k?skill.color:'transparent'}`, background:'none', color:activeTab===k?skill.color:'#5a6a88', padding:'13px 18px', fontSize:13, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:'nowrap', fontWeight:activeTab===k?600:400, transition:'color .15s' }}>
            {TAB_LABELS[k]}
          </button>
        ))}
      </div>

      <div style={{ maxWidth:800, margin:'0 auto', padding:'24px 16px' }}>

        {/* SESSIONS & RECORDINGS */}
        {activeTab === 'sessions' && (
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>Sessions &amp; Recordings</div>
            <div style={{ fontSize:13, color:'#5a6a88', marginBottom:18 }}>
              Recorded sessions and class replays for {skill.label}. New recordings are added by Mr. Ahmed Salem after each class.
            </div>
            {loadingM ? (
              <div style={{ display:'flex', justifyContent:'center', padding:40 }}><Spinner/></div>
            ) : recordings.length > 0 ? (
              <div style={{ display:'grid', gap:10 }}>
                {recordings.map(m => <MaterialCard key={m.id} material={m} color={skill.color}/>)}
              </div>
            ) : (
              <EmptyState
                icon="🎥"
                title="No recordings yet"
                desc="Recorded sessions for this skill will be added here after each class. Check back after your next session."
                waHref={waMsg(`I would like to know if there are recordings available for ${skill.label}.`)}
              />
            )}
          </div>
        )}

        {/* BOOKS & MATERIALS */}
        {activeTab === 'materials' && (
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>Books &amp; Study Materials</div>
            <div style={{ fontSize:13, color:'#5a6a88', marginBottom:18 }}>
              Study guides, books, worksheets, and files for {skill.label}.
            </div>
            {loadingM ? (
              <div style={{ display:'flex', justifyContent:'center', padding:40 }}><Spinner/></div>
            ) : docs.length > 0 ? (
              <div style={{ display:'grid', gap:10 }}>
                {docs.map(m => <MaterialCard key={m.id} material={m} color={skill.color}/>)}
              </div>
            ) : (
              <EmptyState
                icon="📚"
                title="Materials coming soon"
                desc="Study materials for this skill will be uploaded here. Contact Mr. Ahmed if you need specific books or files."
                waHref={waMsg(`I would like to access study materials for ${skill.label}.`)}
              />
            )}
          </div>
        )}

        {/* AI PRACTICE TOOL — Writing Task 2 */}
        {activeTab === 'practice' && skill.hasWritingTool && (
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>AI Writing Practice Tool</div>
            <div style={{ fontSize:13.5, color:'#5a6a88', marginBottom:22, lineHeight:1.7 }}>
              The Writing Task 2 tool uses AI to give you sentence-by-sentence feedback, idea banks for 30+ topic types, and timed exam simulation — all scored across the 4 IELTS writing criteria.
            </div>

            <div style={{ background:'linear-gradient(135deg,#1a2a4a,#2a3a6a)', borderRadius:16, padding:'28px 24px', color:'#fff', marginBottom:16 }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, fontWeight:700, marginBottom:18 }}>✍️ Writing Task 2 Tool</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(185px,1fr))', gap:10, marginBottom:24 }}>
                {[
                  { icon:'✍️', t:'Smart Writing',  s:'Guided practice paragraph by paragraph with formula and structure support' },
                  { icon:'⏱',  t:'Test Mode',       s:'Full 40-minute timed simulation under real exam conditions' },
                  { icon:'🤖', t:'AI Scoring',      s:'Instant scores for Task Achievement, Coherence, Lexical Resource, and Grammar' },
                  { icon:'💡', t:'Idea Banks',       s:'Arguments and vocabulary for 30+ common Task 2 question types' },
                ].map((f,i) => (
                  <div key={i} style={{ background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:10, padding:'14px 12px' }}>
                    <div style={{ fontSize:24, marginBottom:6 }}>{f.icon}</div>
                    <div style={{ fontSize:13, fontWeight:600, color:'#fff', marginBottom:4 }}>{f.t}</div>
                    <div style={{ fontSize:12, color:'rgba(255,255,255,0.65)', lineHeight:1.5 }}>{f.s}</div>
                  </div>
                ))}
              </div>
              <button onClick={onWritingTool}
                style={{ background:'linear-gradient(135deg,#b8860b,#d4a017)', color:'#fff', border:'none', padding:'14px 36px', borderRadius:10, fontSize:15, fontWeight:700, cursor:'pointer', boxShadow:'0 4px 18px rgba(184,134,11,0.4)', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                Open Writing Tool →
              </button>
            </div>

            {onProgressTracker && (
              <div style={{ background:'#fff', border:'1.5px solid #c0d8f8', borderRadius:14, padding:'20px 22px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:14 }}>
                <div>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>📈 My Writing Progress Tracker</div>
                  <div style={{ fontSize:13, color:'#5a6a88', lineHeight:1.65, maxWidth:420 }}>
                    Track your band scores, criterion trends, and essay history. See your strengths, focus areas, and progress across all 4 IELTS writing descriptors.
                  </div>
                </div>
                <button onClick={onProgressTracker}
                  style={{ background:'#1a2a4a', color:'#fff', border:'none', padding:'12px 24px', borderRadius:9, fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:'nowrap', boxShadow:'0 2px 8px rgba(26,42,74,0.2)' }}>
                  Open Tracker →
                </button>
              </div>
            )}
          </div>
        )}

        {/* SPEAKING MOCK EXAM — Book tab */}
        {activeTab === 'mock' && skill.hasMockExam && (
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>Book a Speaking Mock Exam</div>
            <div style={{ fontSize:13.5, color:'#5a6a88', marginBottom:20, lineHeight:1.7 }}>
              Book a full IELTS Speaking mock exam with our certified IELTS examiner. You will receive a detailed examiner report with band scores for all 4 criteria on WhatsApp.
            </div>

            {/* Exam quota badge */}
            {speakingIncluded != null && (
              <div style={{ background:'#fff8f0', border:'1.5px solid #f0d890', borderRadius:10, padding:'12px 18px', marginBottom:18, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                <div style={{ fontSize:13.5, color:'#2a3a5a', fontWeight:600 }}>
                  🗣️ Speaking Mock Exams
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ display:'flex', gap:5 }}>
                    {Array.from({ length: speakingIncluded }).map((_,i) => (
                      <div key={i} style={{ width:14, height:14, borderRadius:'50%', background: i < speakingUsed ? '#b8860b' : '#eee', border:'1.5px solid #f0d890' }}/>
                    ))}
                  </div>
                  <span style={{ fontSize:12.5, color:'#5a6a88', fontFamily:"'JetBrains Mono',monospace" }}>
                    {speakingUsed}/{speakingIncluded} used
                  </span>
                </div>
              </div>
            )}

            <div style={{ background:'#fff', border:'1.5px solid #f0d890', borderRadius:14, padding:'24px', marginBottom:16 }}>
              <div style={{ marginBottom:18 }}>
                {[
                  '15-minute full exam — Part 1 (Introduction), Part 2 (Topic Card), Part 3 (Discussion)',
                  'Scored on all 4 IELTS criteria: Fluency & Coherence, Lexical Resource, Grammar, Pronunciation',
                  'Individual band score per criterion + overall Speaking band estimate',
                  'Personalised examiner feedback and improvement tips',
                  'Full report sent directly to your WhatsApp within 24 hours',
                ].map((f,i) => (
                  <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start', marginBottom:8, fontSize:13.5, color:'#2a3a5a', lineHeight:1.55 }}>
                    <span style={{ color:'#b8860b', flexShrink:0, marginTop:1 }}>✓</span>{f}
                  </div>
                ))}
              </div>
              <a href={waMsg('I would like to book a Speaking Mock Exam. Please let me know the available times and dates.')}
                target="_blank" rel="noreferrer"
                style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, width:'100%', background:'#25d366', color:'#fff', padding:'13px 0', borderRadius:9, fontSize:14, fontWeight:700, textDecoration:'none', boxShadow:'0 3px 12px rgba(37,211,102,0.3)', boxSizing:'border-box' }}>
                <WAIcon size={15}/> Book a Speaking Mock Exam
              </a>
            </div>
            <div style={{ background:'#f7f9fc', border:'1px solid #dde3ed', borderRadius:9, padding:'12px 16px', fontSize:12.5, color:'#5a6a88', lineHeight:1.6 }}>
              ℹ️ {speakingIncluded != null
                ? `Your package includes ${speakingIncluded} Speaking Mock Exams. You have used ${speakingUsed} so far. Contact Mr. Ahmed to confirm your next available slot.`
                : 'Book through WhatsApp — Mr. Ahmed will confirm your slot and connect you with the examiner.'}
            </div>
          </div>
        )}

        {/* SPEAKING PROGRESS TRACKER */}
        {activeTab === 'progress' && skill.hasMockExam && (
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>My Speaking Progress</div>
            <div style={{ fontSize:13.5, color:'#5a6a88', marginBottom:20, lineHeight:1.7 }}>
              Track your Speaking band scores across all 4 IELTS criteria. Scores are added by the examiner after each mock exam.
            </div>
            <SpeakingProgressView exams={completedExams}/>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main dashboard (overview) ─────────────────────────────────────────────────
export default function IELTSDashboard({ student, onLogout, onWritingTool, onPlacementTest, onAcademy, onProgressTracker }) {
  const [activeSkill,   setActiveSkill]   = useState(null);
  const [showAccount,   setShowAccount]   = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [upcoming,      setUpcoming]      = useState([]);
  const [speakingExams, setSpeakingExams] = useState([]);
  const [tier,          setTier]          = useState(null);
  const [loading,       setLoading]       = useState(true);

  useEffect(() => {
    if (!student?.id) { setLoading(false); return; }
    async function load() {
      const [subRes, bkgRes, spkRes, tierRes] = await Promise.all([
        supabase.from('subscriptions').select('*').eq('student_id', student.id).eq('status', 'active'),
        supabase.from('bookings')
          .select('*, teachers(name,zoom_meeting_id), time_slots(start_time,end_time)')
          .eq('student_id', student.id)
          .eq('status', 'confirmed')
          .order('created_at', { ascending: false })
          .limit(10),
        supabase.from('speaking_exams')
          .select('*')
          .eq('student_id', student.id)
          .order('scheduled_at', { ascending: true }),
        student.subscription_type
          ? supabase.from('subscription_tiers')
              .select('*')
              .ilike('name', `%${student.subscription_type.includes('premium') ? 'premium' : 'standard'}%`)
              .eq('is_active', true)
              .limit(1)
          : Promise.resolve({ data: [] }),
      ]);
      setSubscriptions(subRes.data || []);
      const now = new Date().toISOString();
      setUpcoming((bkgRes.data || []).filter(b => b.time_slots?.start_time > now).sort((a,b)=>a.time_slots.start_time.localeCompare(b.time_slots.start_time)).slice(0, 3));
      setSpeakingExams(spkRes.data || []);
      setTier((tierRes.data || [])[0] || null);
      setLoading(false);
    }
    load();
  }, [student?.id, student?.subscription_type]);

  if (activeSkill) {
    const skill = SKILLS.find(s => s.id === activeSkill);
    return (
      <SkillView
        skill={skill}
        student={student}
        tier={tier}
        speakingExams={speakingExams}
        onBack={() => setActiveSkill(null)}
        onWritingTool={onWritingTool}
        onProgressTracker={onProgressTracker}
      />
    );
  }

  const totalHours     = subscriptions.reduce((s,x) => s + (Number(x.total_hours)||0), 0);
  const usedHours      = subscriptions.reduce((s,x) => s + (Number(x.used_hours)||0), 0);
  const remainingHours = Math.max(0, totalHours - usedHours);
  const isPremium      = student?.subscription_type?.toLowerCase().includes('premium');

  // Exam usage from tier
  const writingIncluded  = tier?.writing_exams_included;
  const speakingIncluded = tier?.speaking_exams_included;
  const speakingUsed     = speakingExams.filter(e => e.status === 'completed').length;

  function waHref(msg) {
    return `https://wa.me/${ADMIN_WA}?text=${encodeURIComponent(`Hello Mr. Ahmed! My name is ${student?.name || 'a student'}. ${msg}`)}`;
  }

  function fmtSlot(b) {
    if (!b.time_slots?.start_time) return 'Date TBC';
    const d = new Date(b.time_slots.start_time);
    return d.toLocaleString('en-GB', { weekday:'short', day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' });
  }

  function buildZoom(b) {
    if (b.zoom_link) return b.zoom_link;
    if (b.teachers?.zoom_meeting_id) return `https://zoom.us/j/${b.teachers.zoom_meeting_id.replace(/\s/g,'')}`;
    return null;
  }

  return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>

      {/* Header */}
      <div style={{ background:'linear-gradient(135deg,#1a2a4a,#2a3a6a)', padding:'14px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ background:'rgba(255,255,255,0.12)', borderRadius:8, padding:'4px 8px', display:'flex', alignItems:'center', flexShrink:0 }}>
            <img src="/logo.png" alt="IELTS Compass" style={{ height:32, objectFit:'contain' }}/>
          </div>
          <div>
            <div style={{ fontWeight:700, fontSize:16, color:'#fff', lineHeight:1.2 }}>Welcome back, {student?.name}</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.55)', marginTop:2 }}>
              {isPremium ? 'IELTS Premium' : 'IELTS Standard'}
              {totalHours > 0 && ` · ${remainingHours.toFixed(0)}h remaining`}
            </div>
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <button onClick={() => { setShowAccount(a => !a); setActiveSkill(null); }}
            style={{ background: showAccount ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', color:'#fff', padding:'7px 13px', borderRadius:7, cursor:'pointer', fontSize:12, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            ⚙️ Account
          </button>
          <button onClick={onLogout}
            style={{ background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)', color:'#fff', padding:'7px 13px', borderRadius:7, cursor:'pointer', fontSize:12, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            Sign Out
          </button>
        </div>
      </div>

      <div style={{ maxWidth:960, margin:'0 auto', padding:'24px 16px' }}>

        {showAccount ? (
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:22 }}>
              <button onClick={() => setShowAccount(false)}
                style={{ background:'none', border:'1px solid #dde3ed', borderRadius:8, padding:'7px 14px', cursor:'pointer', fontSize:13, color:'#5a6a88', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                ← Back to Dashboard
              </button>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, color:'#1a2a4a' }}>Account Settings</div>
            </div>
            <ChangePasswordCard studentId={student?.id}/>
          </div>
        ) : loading ? (
          <div style={{ display:'flex', justifyContent:'center', padding:32 }}><Spinner/></div>
        ) : (
          <>
            {/* Subscription usage bar */}
            {(writingIncluded != null || speakingIncluded != null) && (
              <div style={{ background:'#fff', border:'1px solid #dde3ed', borderRadius:14, padding:'18px 20px', marginBottom:22, boxShadow:'0 2px 10px rgba(26,42,74,0.06)' }}>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, fontWeight:700, color:'#1a2a4a', marginBottom:14 }}>
                  📋 Package Summary — {isPremium ? 'IELTS Premium' : 'IELTS Standard'}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:14 }}>
                  {writingIncluded != null && (
                    <div style={{ background:'#eef2f9', borderRadius:10, padding:'14px 16px' }}>
                      <div style={{ fontSize:12, color:'#5a6a88', fontWeight:600, marginBottom:6 }}>✍️ Writing Exams</div>
                      <div style={{ fontSize:22, fontWeight:700, color:'#1a2a4a', fontFamily:"'JetBrains Mono',monospace", lineHeight:1 }}>{writingIncluded}</div>
                      <div style={{ fontSize:11, color:'#9aa3b8', marginTop:4 }}>included in your package</div>
                    </div>
                  )}
                  {speakingIncluded != null && (
                    <div style={{ background:'#fff8f0', borderRadius:10, padding:'14px 16px' }}>
                      <div style={{ fontSize:12, color:'#5a6a88', fontWeight:600, marginBottom:6 }}>🗣️ Speaking Mock Exams</div>
                      <div style={{ display:'flex', alignItems:'baseline', gap:6, marginBottom:4 }}>
                        <div style={{ fontSize:22, fontWeight:700, color:'#b8620a', fontFamily:"'JetBrains Mono',monospace", lineHeight:1 }}>{speakingUsed}</div>
                        <div style={{ fontSize:14, color:'#9aa3b8', fontFamily:"'JetBrains Mono',monospace" }}>/ {speakingIncluded}</div>
                      </div>
                      <div style={{ background:'#eee', borderRadius:100, height:5, overflow:'hidden', marginTop:6 }}>
                        <div style={{ width:`${Math.min(100,(speakingUsed/speakingIncluded)*100)}%`, background:'#b8860b', height:'100%', borderRadius:100 }}/>
                      </div>
                      <div style={{ fontSize:11, color:'#9aa3b8', marginTop:4 }}>{speakingIncluded - speakingUsed} remaining</div>
                    </div>
                  )}
                  {tier?.grace_period_days != null && student?.subscription_end && (
                    <div style={{ background:'#e8f5ee', borderRadius:10, padding:'14px 16px' }}>
                      <div style={{ fontSize:12, color:'#5a6a88', fontWeight:600, marginBottom:6 }}>🕐 Grace Period</div>
                      <div style={{ fontSize:22, fontWeight:700, color:'#1a7a4a', fontFamily:"'JetBrains Mono',monospace", lineHeight:1 }}>{tier.grace_period_days}</div>
                      <div style={{ fontSize:11, color:'#9aa3b8', marginTop:4 }}>days after course ends</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Upcoming sessions */}
            {upcoming.length > 0 && (
              <div style={{ background:'#fff', border:'1px solid #dde3ed', borderRadius:14, padding:'18px 20px', marginBottom:22, boxShadow:'0 2px 10px rgba(26,42,74,0.06)' }}>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:17, fontWeight:700, color:'#1a2a4a', marginBottom:12 }}>📅 Upcoming Sessions</div>
                {upcoming.map(b => {
                  const zoom = buildZoom(b);
                  return (
                    <div key={b.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #f0f4f8', flexWrap:'wrap', gap:8 }}>
                      <div>
                        <div style={{ fontSize:13.5, fontWeight:600, color:'#1a2233' }}>{b.teachers?.name || 'Teacher'}</div>
                        <div style={{ fontSize:12.5, color:'#5a6a88' }}>{fmtSlot(b)}</div>
                      </div>
                      {zoom && (
                        <a href={zoom} target="_blank" rel="noreferrer"
                          style={{ background:'#1a5fa8', color:'#fff', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:700, textDecoration:'none', display:'flex', alignItems:'center', gap:6 }}>
                          🎥 Join Zoom
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Book Private Sessions */}
            <div style={{ background:'linear-gradient(135deg,#b8860b,#d4a017)', borderRadius:14, padding:'20px 22px', marginBottom:22, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:14, boxShadow:'0 4px 18px rgba(184,134,11,0.25)' }}>
              <div>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#fff', marginBottom:4 }}>📅 Book Private Sessions</div>
                <div style={{ fontSize:13, color:'rgba(255,255,255,0.85)', lineHeight:1.65, maxWidth:480 }}>
                  Schedule your next one-to-one lessons. Contact Mr. Ahmed Salem on WhatsApp to confirm your availability and book upcoming sessions.
                </div>
              </div>
              <a href={waHref('I would like to book my next private sessions. Please let me know your availability.')} target="_blank" rel="noreferrer"
                style={{ background:'#fff', color:'#b8860b', padding:'12px 24px', borderRadius:9, fontSize:14, fontWeight:700, textDecoration:'none', display:'flex', alignItems:'center', gap:8, whiteSpace:'nowrap', boxShadow:'0 2px 8px rgba(0,0,0,0.12)', flexShrink:0 }}>
                <WAIcon size={15}/> Book on WhatsApp
              </a>
            </div>

            {/* Course heading */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>My IELTS Course</div>
              <div style={{ fontSize:14, color:'#5a6a88' }}>Select a skill to access your sessions, materials, and practice tools.</div>
            </div>

            {/* Skill grid */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(270px,1fr))', gap:16, marginBottom:28 }}>
              {SKILLS.map(sk => (
                <button key={sk.id} onClick={() => setActiveSkill(sk.id)}
                  style={{ background:'#fff', border:`2px solid ${sk.border}`, borderRadius:16, padding:'22px 20px', textAlign:'left', cursor:'pointer', transition:'all .18s', boxShadow:'0 2px 10px rgba(26,42,74,0.06)', position:'relative', width:'100%' }}
                  onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow=`0 8px 24px ${sk.color}22`; e.currentTarget.style.borderColor=sk.color+'88'; }}
                  onMouseLeave={e=>{ e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 2px 10px rgba(26,42,74,0.06)'; e.currentTarget.style.borderColor=sk.border; }}>
                  {sk.hasWritingTool && (
                    <div style={{ position:'absolute', top:14, right:14, fontSize:9.5, fontWeight:700, color:sk.color, background:`${sk.color}18`, border:`1px solid ${sk.color}44`, borderRadius:100, padding:'2px 8px', fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.07em' }}>
                      AI POWERED
                    </div>
                  )}
                  {sk.hasMockExam && speakingUsed > 0 && (
                    <div style={{ position:'absolute', top:14, right:14, fontSize:9.5, fontWeight:700, color:'#b8620a', background:'#fff8f0', border:'1px solid #f0d890', borderRadius:100, padding:'2px 8px', fontFamily:"'JetBrains Mono',monospace" }}>
                      {speakingUsed} exam{speakingUsed!==1?'s':''} done
                    </div>
                  )}
                  <div style={{ fontSize:40, marginBottom:12 }}>{sk.icon}</div>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:21, fontWeight:700, color:'#1a2233', marginBottom:6 }}>{sk.label}</div>
                  <div style={{ fontSize:12.5, color:'#5a6a88', lineHeight:1.65, marginBottom:16 }}>{sk.desc}</div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    <span style={{ fontSize:11, color:sk.color, background:sk.bg, border:`1px solid ${sk.border}`, borderRadius:100, padding:'2px 10px', fontWeight:600 }}>Sessions</span>
                    <span style={{ fontSize:11, color:sk.color, background:sk.bg, border:`1px solid ${sk.border}`, borderRadius:100, padding:'2px 10px', fontWeight:600 }}>Materials</span>
                    {sk.hasWritingTool && <span style={{ fontSize:11, color:sk.color, background:sk.bg, border:`1px solid ${sk.border}`, borderRadius:100, padding:'2px 10px', fontWeight:600 }}>AI Practice</span>}
                    {sk.hasMockExam  && <span style={{ fontSize:11, color:sk.color, background:sk.bg, border:`1px solid ${sk.border}`, borderRadius:100, padding:'2px 10px', fontWeight:600 }}>Mock Exam</span>}
                    {sk.hasMockExam  && <span style={{ fontSize:11, color:sk.color, background:sk.bg, border:`1px solid ${sk.border}`, borderRadius:100, padding:'2px 10px', fontWeight:600 }}>My Progress</span>}
                  </div>
                </button>
              ))}
            </div>

            {/* Academy shortcut */}
            {onAcademy && (
              <div style={{ background:'#fff', border:'1.5px solid #e8edf5', borderRadius:12, padding:'16px 20px', marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12, boxShadow:'0 1px 6px rgba(26,42,74,0.05)' }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:'#1a2233', marginBottom:2 }}>🏫 Explore the Academy</div>
                  <div style={{ fontSize:12.5, color:'#5a6a88', lineHeight:1.55 }}>Browse all available courses, meet our teaching team, and discover additional programmes.</div>
                </div>
                <button onClick={onAcademy}
                  style={{ background:'#1a2a4a', color:'#fff', border:'none', padding:'10px 20px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:'nowrap' }}>
                  Visit Academy →
                </button>
              </div>
            )}

            {/* Contact strip */}
            <div style={{ background:'linear-gradient(135deg,#1a2a4a,#2a3a6a)', borderRadius:14, padding:'20px 22px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:14 }}>
              <div>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#fff', marginBottom:3 }}>Need help or have a question?</div>
                <div style={{ fontSize:13, color:'rgba(255,255,255,0.6)' }}>Contact Mr. Ahmed Salem directly on WhatsApp for anything about your course.</div>
              </div>
              <a href={waHref('I have a question about my IELTS course.')} target="_blank" rel="noreferrer"
                style={{ background:'#25d366', color:'#fff', padding:'12px 22px', borderRadius:9, fontSize:13.5, fontWeight:700, textDecoration:'none', display:'flex', alignItems:'center', gap:8, boxShadow:'0 3px 14px rgba(37,211,102,0.35)', whiteSpace:'nowrap' }}>
                <WAIcon size={15}/> Contact Mr. Ahmed
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
